import { useEffect, useState } from 'react';
import { IonCheckbox, IonText } from '@ionic/react';
import { Page } from '../components/page/Page';
import { TrackingService } from '../support/tracking/TrackingService';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { InsuranceState } from './InsuranceState';
import { useSubscription } from '@meraki-internal/state';
import { InsurancePolicyList } from './InsurancePolicyList';
import { NewPolicyButton } from './NewPolicyButton';
import { InsuranceErrorPage } from './InsuranceErrorPage';
import { Spinner } from '../components/spinner/Spinner';
import { Refresher } from '../components/refresher/Refresher';
import { useRefresher } from '../support/hooks/useRefresher';
import { RefreshTimer } from '../components/refresher/RefreshTimer';
import { Logger } from '../support/debug/Logger';
import { tryAndCatch } from '../support/helpers/tryAndCatch';
import { IInsurancePolicy } from './IInsurancePolicy';
import { getPendingPolicyRefreshIntervalMs } from './policyHelpers';
import { DoneIcon } from '../components/icons/Icons';
import { Badge } from '../components/badge/Badge';

const MIN_ANNUAL_PRICE = 450;
const MIN_MONTHLY_PRICE = 42;

const Benefit: React.FC<{text: string}> = ({ text }) => (
    <div style={{marginTop: 10, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10, fontSize: 14}}>
        <DoneIcon variant="filled" size="medium" color="insurance" />
        <b>{text}</b>
    </div>
);

const PlanCard: React.FC<{label: string, selected: boolean, onClick: () => void, startingPrice: number, fullPrice?: number, billed: 'annually' | 'monthly' }> = ({ label, selected, onClick, startingPrice, fullPrice, billed }) => {
    const discount = fullPrice ? fullPrice - startingPrice : 0;
    return (
        <div style={{marginTop: 10, borderRadius: 12, border: '1px solid #e0e0e0', width: '46%', height: 125}} onClick={onClick}>

            <div style={{textAlign: 'center', margin: '0 auto', height: 22, transform: 'translateY(-11px)'}}>
                {Boolean(discount) &&
                    <Badge size="small" color="insurance" saturated>${discount} Savings</Badge>
                }
            </div>

            <div style={{height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', marginTop: -20, padding: 10, paddingTop: 15}}>
                <div>
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <b style={{fontSize: 20}}>{label}</b>
                        {/* use zIndex to prevent directly clicking on the checkbox so it can't be deselected (behaves like a radio button) */}
                        <IonCheckbox mode="ios" color="insurance" style={{zIndex: -1, '--size': '25px', '--checkmark-width': '2px'}} checked={selected}/>
                    </div>

                    <div style={{display: 'flex', flexDirection: 'column', marginTop: 10, marginBottom: 5}}>
                        <IonText color="dark"><b style={{display: 'flex', alignItems: 'center'}}><span>${startingPrice}</span><small>&nbsp;/&nbsp;starting at</small></b></IonText>
                        {Boolean(fullPrice) &&
                            <IonText color="medium"><b><s><small>${fullPrice}&nbsp;/&nbsp;YR</small></s></b></IonText>
                        }
                    </div>
                </div>

                <div><IonText color="medium"><small><b>Billed {billed}</b></small></IonText></div>
            </div>

        </div>
    );
};

export const InsuranceLandingPage: React.FC = () => {
    const tracker = useInstance(TrackingService);
    const log = useInstance(Logger);

    const insuranceState = useInstance(InsuranceState);
    useSubscription(() => insuranceState);

    const { onRefresh, isRefreshing } = useRefresher(insuranceState.load);

    const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual'>('monthly');

    useEffect(() => {
        Promise.resolve().then(async () => {
            await tryAndCatch(insuranceState.load, log.error);
            if (insuranceState.state.loaded) {
                const pageName = (insuranceState.state.policies!.length ? 'Insurance Dashboard' : 'Insurance Marketing');
                tracker.startPageView(pageName);
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!insuranceState.state.loaded && insuranceState.state.error) {
        return <InsuranceErrorPage />;
    }

    let policies: IInsurancePolicy[] = [];
    let pageTitle = 'Insurance';

    if (insuranceState.state.loaded) {
        policies = insuranceState.getPolicies();
        pageTitle = (policies.length ? 'Insurance Dashboard' : 'Get a Quote');
    }

    const getRefreshIntervalMs = () => {
        const intervals = policies.map(getPendingPolicyRefreshIntervalMs);
        return Math.min(...intervals);
    };

    return (
        <>
            {/* image needs to be outside page so it doesn't move when page is pulled down */}
            {insuranceState.state.loaded && policies.length === 0 &&
                <img style={{zIndex: 1, position: 'absolute', height: 120, top: 0, right: 0}} src="./assets/images/insurance/flying-drone.png" alt="Flying drone" />
            }

            <Page title={pageTitle}>

                <Refresher onRefresh={onRefresh} />

                {policies.some(p => p.pending) &&
                    <RefreshTimer getIntervalMs={getRefreshIntervalMs} handler={insuranceState.load} />
                }

                {insuranceState.state.loaded &&
                    <>
                        {policies.length === 0 &&
                            <div style={{margin: '90px 20px 0'}}>
                                <h2 style={{marginBottom: 0}}><b>Protect your passion</b></h2>
                                <h2 style={{marginTop: 0}}><b>Get <IonText color="primary">drone insurance.</IonText></b></h2>

                                <p style={{marginTop: 10}}>Get quick and affordable drone coverage in less than 7 minutes.</p>

                                <div style={{marginTop: 25, marginBottom: 35}}>
                                    <Benefit text="Get up to $10 Million in liability" />
                                    <Benefit text="Add hull coverage for drones and equipment" />
                                    <Benefit text="Easily access your policy and docs any time!" />
                                </div>

                                <div>
                                    {!insuranceState.hasPendingApplication() &&
                                        <p style={{fontSize: 18, textAlign: 'center'}}><b>Start by selecting a plan and state.</b></p>
                                    }

                                    <div style={{margin: '25px 0 35px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                        <PlanCard label="Annual" selected={selectedPlan === 'annual'} onClick={() => setSelectedPlan('annual')} startingPrice={MIN_ANNUAL_PRICE} fullPrice={MIN_MONTHLY_PRICE * 12} billed="annually" />
                                        <PlanCard label="Monthly" selected={selectedPlan === 'monthly'} onClick={() => setSelectedPlan('monthly')} startingPrice={MIN_MONTHLY_PRICE} billed="monthly" />
                                    </div>
                                </div>
                            </div>
                        }

                        <NewPolicyButton />

                        <InsurancePolicyList policies={policies} />

                        <div style={{margin: '30px 20px', lineHeight: 0.75}}>
                            <small><i>
                                Insurance coverage is offered through Brokery, LLC ("Brokery"), a licensed insurance producer.
                                All insurance-related information is provided by Brokery and does not in any way alter or amend
                                the terms, conditions, or exclusions of any insurance policy. Insurance coverage is not available
                                in all jurisdictions or to all customers. The above does not in any way constitute an endorsement
                                or referral of Brokery's products or services.
                            </i></small>
                        </div>
                    </>
                }

                {insuranceState.state.loading && !isRefreshing &&
                    <Spinner name="crescent" centered />
                }

            </Page>
        </>
    );
};
